import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import TemplateListagem from "../../../../../../components/BasicComponents/DisplayDados/TemplateListagem";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { TLancamentos } from "../../../../../../types/api/financeiro/pesquisar-lancamentos";
import { useCallback, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { enqueueSnackbar } from "notistack";
import { useConfirm } from "material-ui-confirm";
import moment from "moment";
import SearchWithFilters from "../../../../../../components/BasicComponents/SearchWithFilters";
import DialogFiltersLancamentos from "../DialogFiltersLancamentos";
import { TCaixaBancoContas } from "../../../../../../types/api/financeiro/caixa-banco-contas";
import useDebounce from "../../../../../../lib/useDebounce";
import ChipList from "../../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../../helpers/getChipFilterItems";

export default function Listagem() {

  const [palavraChave, setPalavraChave] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openFiltros, setOpenFiltros] = useState(false);
  const [filters, setFilters] = useState<TFiltrosLancamentos>({});

  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const debouncedSearchTerm = useDebounce(palavraChave);

  //queries e mutations
  const { data, isLoading } = useQuery({
    queryKey: ["pesquisar-lancamentos", page, rowsPerPage, filters, debouncedSearchTerm],
    queryFn: async () => {
      const response = await axiosClient.get(
        "/financeiro/caixa-bancos/pesquisar-lancamentos",
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            pesquisar: debouncedSearchTerm || null,
            ...formatarFiltros(filters),
          },
        }
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  const { mutate: deletarLancamento, isLoading: isLoadingDelete } = useMutation(
    {
      mutationKey: "excluir-lancamento",
      mutationFn: (id: string) =>
        axiosClient.delete(`/financeiro/caixa-bancos/excluir-lancamento/${id}`),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao excluir lançamento!", { variant: "error" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries("pesquisar-lancamentos");
        enqueueSnackbar("Exclusão de lançamento realizada com sucesso!", {
          variant: "success",
        });
      },
    }
  );
  //

  const handleDeleteLancamento = useCallback(
    async (id: string) => {
      confirm({
        title: "Confirmação",
        description: "Deseja realmente deletá-lo?",
        confirmationText: "Excluir",
        confirmationButtonProps: { color: "error", variant: "contained" },
        cancellationText: "Cancelar",
        dialogProps: { maxWidth: "xs", fullWidth: true },
      })
        .then(() => deletarLancamento(id))
        .catch(() => { });
    },
    [deletarLancamento, confirm]
  );

  const handleDeleteFilter = (item: string) => {
    const newFiltros = { ...filters };

    if (item.startsWith('Valor:')) {
      delete newFiltros.valor_igual;
      delete newFiltros.valor;
    } else if (item.startsWith('Banco:')) {
      delete newFiltros.banco;
      delete newFiltros.codigo_banco_contem;
    } else {
      // Para outros filtros, encontra a chave correspondente
      Object.entries(filters).forEach(([key, value]) => {
        const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
        const chipText = `${label}`;
        if (chipText === item.split(':')[0]) {
          delete newFiltros[key as keyof TFiltrosLancamentos];
        }
      });
    }

    setFilters(newFiltros);
  }

  const chipItems = useMemo(() => {
    return getChipFilterItems(
      filters,
      filtersChipLabels,
      ['valor_igual', 'codigo_banco_contem']
    );
  }, [filters]);

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 2,
        }}
      >

        <SearchWithFilters
          palavraChave={palavraChave}
          textFieldProps={{
            label: "Pesquisar",
            placeholder: "Pesquise por histórico, documento ou beneficiário",
            size: "medium",
          }}
          pesquisar={(term) => { setPalavraChave(term) }}
          setTermoPesquisa={setPalavraChave}
          handleOpenFilters={setOpenFiltros}
        />

        <ChipList
          items={chipItems}
          onDelete={handleDeleteFilter}
        />
        <TemplateListagem
          isLoading={isLoading}
          isEmpty={data?.data.length === 0}
        >
          <TableContainer>
            <Table
              size="small"
              sx={{ minWidth: 650 }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Doc.</b>
                  </TableCell>
                  <TableCell>
                    <b>Data</b>
                  </TableCell>
                  <TableCell>
                    <b>Histórico</b>
                  </TableCell>
                  <TableCell>
                    <b>Beneficiario</b>
                  </TableCell>
                  <TableCell>
                    <b>Banco</b>
                  </TableCell>
                  <TableCell>
                    <b>Valor</b>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data?.data.map((row: TLancamentos) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.documento}
                    </TableCell>
                    <TableCell>{moment(row.data_lancamento).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>{row.historico}</TableCell>
                    <TableCell>{row.beneficiario}</TableCell>
                    <TableCell>{row?.banco?.nome}</TableCell>
                    <TableCell align="right">
                      {formatarValorMonetario(row.valor)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteLancamento(row.id)}
                        disabled={isLoadingDelete}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TemplateListagem>

        <TablePagination
          rowsPerPageOptions={[10, 20, 25]}
          component="div"
          disabled={isLoading}
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={() => `Pag. ${page + 1}`}
        />

      </Paper>
      {openFiltros &&
        <DialogFiltersLancamentos
          open={openFiltros}
          onClose={() => setOpenFiltros(false)}
          onConfirm={(filtros) => {
            setOpenFiltros(false);
            setPage(0);
            setFilters(filtros);
          }}
          filtrosAtuais={filters}
        />}
    </>
  );
}
export interface TFiltrosLancamentos {
  valor?: string;
  valor_igual?: number;
  banco?: TCaixaBancoContas;
  codigo_banco_contem?: number[];
  data_movimentacao_inicio?: string;
  data_movimentacao_fim?: string;
}

const filtersChipLabels: { [key in keyof Omit<TFiltrosLancamentos, 'valor_menor_igual' | 'valor_maior_igual' | 'codigo_banco_contem'>]: string } = {
  data_movimentacao_inicio: 'Data movimentação início',
  data_movimentacao_fim: 'Data movimentação fim',
  banco: 'Banco',
  valor: 'Valor',
};

function formatarFiltros(filtros: TFiltrosLancamentos) {
  const formattedFilters: any = { ...filtros };

  // Remove o objeto banco e mantém apenas codigo_banco_contem
  delete formattedFilters.banco;

  // Remove campos que não devem ser enviados
  delete formattedFilters.valor;

  return formattedFilters;
}
